import Dialog from '../../1. Core/Controls/Dialog/Dialog';
import NP from '../../1. Core/NP';
import Select from '../../1. Core/Controls/Select/Select';
import Button from '../../1. Core/Controls/Button/Button';
import DataTable from '../../1. Core/Controls/DataTable/DataTable';
import IconToggleButton from '../../1. Core/Controls/IconToggleButton/IconToggleButton';
import {getCountries} from '../Util';
import {IState} from '../../2. Athletes/StatesList';
import BroadcastProvidersDialog, {BroadcastProvider, loadBroadcastProviders} from './BroadcastProvidersDialog';

interface TVStreamData {
	CountryId: string;
	ProviderId?: string | number | null;
	CountryName?: string;
	ProviderName?: string;
	/** Legacy rows only — no longer edited in admin. */
	TVStreamUrl?: string;
}

class TVStreamEditDialog extends Dialog {
	private _country: Select;
	private _provider: Select;
	private _CountryId: string | null;
	private _ProviderId: string | number | null;
	private _IsEdit: boolean;
	private _countries: IState[] = [];
	private _providers: BroadcastProvider[] = [];

	_initFields(settings: object) {
		this._CountryId = null;
		this._ProviderId = null;
		this._IsEdit = false;
		super._initFields(settings);
		this._Mode = 'FullHeight';
		this._Caption = 'Add TV/Stream';
		this._CloseOnFrameMouseDown = true;
		this._ShowHeader = true;
		this._ShowCloseButton = true;
		this._OkRK = 'Save';
		this._AutoDestroy = true;
	}

	_render() {
		super._render();

		this._country = new Select({
			FloatingLabel: NP.RM.getString('Country'),
			ParentNode: this._contentNode,
		});

		this._provider = new Select({
			FloatingLabel: 'Streaming platform',
			ParentNode: this._contentNode,
		});

		getCountries(true).then((res: IState[]) => {
			this._countries = res;
			const items = res.map(state => ({Value: state.Id, FontImage: NP.FontImages.geo, Content: state.Name}));
			this._country.setItems(items);
			this._country.setValue(this._CountryId);
		});

		loadBroadcastProviders().then((providers) => {
			this._providers = providers.filter(p => p.active);
			const items = providers.map(p => ({
				Value: p.id,
				Content: p.name,
				FontImage: p.logoUrl ? null : NP.FontImages.tv,
			}));
			this._provider.setItems(items);
			if (this._ProviderId != null) {
				this._provider.setValue(this._ProviderId);
			}
		});

		new Button({
			Content: 'Manage platforms',
			ParentNode: this._contentNode,
			EvClick: () => {
				new BroadcastProvidersDialog({
					EvClose: () => {
						loadBroadcastProviders(true).then((providers) => {
							this._providers = providers.filter(p => p.active);
							const items = providers.map(p => ({
								Value: p.id,
								Content: p.name,
								FontImage: p.logoUrl ? null : NP.FontImages.tv,
							}));
							this._provider.setItems(items);
						});
					},
				}).show();
			},
		});

		if (this._IsEdit) {
			const btn = new Button({
				RK: 'Remove',
				EvClick: () => {
					this.EvOkButtonClick.fire(this, {Remove: true});
					this.hide();
				},
			});
			btn.putToNode(this._buttonsNode, this._cancelButton.getRoot());
		}
	}

	_onOkButtonClick() {
		if (!this._country.getValue()) {
			NP.showMessage({Content: 'Select country'});
			return;
		}
		if (!this._provider.getValue()) {
			NP.showMessage({Content: 'Select streaming platform'});
			return;
		}
		const country = this._countries.find(c => String(c.Id) === String(this._country.getValue()));
		const provider = this._providers.find(p => String(p.id) === String(this._provider.getValue()));
		this.EvOkButtonClick.fire(this, {
			CountryId: this._country.getValue(),
			ProviderId: this._provider.getValue(),
			CountryName: country?.Name,
			ProviderName: provider?.name,
		});
		this.hide();
	}
}

export default class TVStreamListDialog extends Dialog {
	private _dataTable: DataTable;
	private _TVStreamData: string;
	private _countriesById: Record<string, string> = {};
	private _providersById: Record<string, string> = {};

	_initFields(settings: object) {
		this._TVStreamData = null;
		super._initFields(settings);
		this._Mode = 'FullHeight';
		this._Caption = 'TV/Stream';
		this._CloseOnFrameMouseDown = true;
		this._ShowHeader = true;
		this._ShowCloseButton = true;
		this._ShowButtons = true;
		this._OkRK = 'Save';
		this._AutoDestroy = true;
		this._Width = 750;
		this._ContentAutoFit = true;
	}

	_render() {
		super._render();
		this.addClass('nopad');

		const rows: TVStreamData[] = this._TVStreamData ? JSON.parse(this._TVStreamData) : [];

		this.setContent(
			(this._dataTable = new DataTable({
				Class: 'app-list--avatars mdc-data-table--margin data-table-battle',
				MultiSelect: false,
				ShowRowIndex: false,
				ImageWidth: 32,
				Columns: [
					{Content: 'Country', PropertyContent: 'CountryName', Width: '45%'},
					{Content: 'Platform', PropertyContent: 'ProviderName', Width: '45%'},
				],
				ShowPaginator: false,
				EvItemClick: (s: object, e: { Item: TVStreamData }) => {
					new TVStreamEditDialog({
						IsEdit: true,
						CountryId: e.Item.CountryId,
						ProviderId: e.Item.ProviderId,
						EvOkButtonClick: (sender: object, args: any) => {
							if (args.Remove) {
								NP.removeFromArray(rows, e.Item);
							} else {
								e.Item.CountryId = args.CountryId;
								e.Item.ProviderId = args.ProviderId;
								e.Item.CountryName = args.CountryName;
								e.Item.ProviderName = args.ProviderName;
							}
							this.decorateRows(rows);
							this._dataTable.setRows(rows);
						},
					}).show();
				},
			})),
		);

		this._dataTable.setRows(rows);

		this._topAppBar.addItem(
			new IconToggleButton({
				FontImage: NP.FontImages.add,
				Tooltip: NP.RM.getString('Create'),
				EvClick: () => {
					new TVStreamEditDialog({
						EvOkButtonClick: (sender: object, e: TVStreamData) => {
							rows.push(e);
							this.decorateRows(rows);
							this._dataTable.setRows(rows);
						},
					}).show();
				},
			}),
		);

		this._topAppBar.addItem(
			new IconToggleButton({
				FontImage: NP.FontImages.settings,
				Tooltip: 'Manage platforms',
				EvClick: () => {
					new BroadcastProvidersDialog().show();
				},
			}),
		);

		Promise.all([getCountries(true), loadBroadcastProviders()]).then(([countries, providers]) => {
			countries.forEach((c: IState) => {
				this._countriesById[String(c.Id)] = c.Name;
			});
			providers.forEach((p) => {
				this._providersById[String(p.id)] = p.name;
			});
			this.decorateRows(rows);
			this._dataTable.setRows(rows);
		});
	}

	decorateRows(rows: TVStreamData[]) {
		rows.forEach((row) => {
			if (!row.CountryName) {
				row.CountryName = this._countriesById[String(row.CountryId)] ?? String(row.CountryId);
			}
			if (!row.ProviderName && row.ProviderId != null) {
				row.ProviderName = this._providersById[String(row.ProviderId)] ?? '';
			}
		});
	}

	_onOkButtonClick() {
		const rows = this._dataTable.getRows() as TVStreamData[];
		const payload = rows.map(({CountryId, ProviderId}) => ({
			CountryId: Number(CountryId),
			ProviderId: ProviderId != null ? Number(ProviderId) : null,
		}));
		this.EvOkButtonClick.fire(this, {Value: JSON.stringify(payload)});
		this.hide();
	}
}